import * as formik from 'formik';
import { Field, Form } from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { IDropdownOption } from 'office-ui-fabric-react';
import { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../api/generated';
import { KendoLoading } from '../../../../components';
import ListItemCreateButton from '../../../../components/custom/ListItemCreateButton';
import { ILocationItem } from '../../../../components/custom/LocationTree';
import { useConfirmation } from '../../../../components/dialogs';
import { LocationContext } from '../../../../components/LocationContext';
import { useWindow } from '../../../../components/windows/WindowContext';
import { locationType } from '../../../../enums';
import LocationTreeNodeCreateWindow from './creationWindows/LocationTreeNodeCreateWindow';

const validationSchema = Yup.object<ILocationItem>().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    activeTicketPrintingTimeBeforeVisit: Yup.string()
        .nullable()
        .when('isActiveTicketPrintingTimeBeforeVisitEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        }),
    activeTicketPrintingTimeAfterVisit: Yup.string()
        .nullable()
        .when('isActiveTicketPrintingTimeAfterVisitEnabled', {
            is: true,
            then: Yup.string().required('Laukas yra privalomas'),
            otherwise: Yup.string()
        })
});

const toggleStyles = {
    root: {
        marginTop: '35px'
    }
};

const LocationsPageLocation: FC = () => {
    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const [levelOptions, setLevelOptions] = useState<fabric.IDropdownOption[] | null>(null);

    const { state, getLocations, deleteLocation, getSelectedLocation } = useContext(LocationContext);
    const [isLoading, setIsLoading] = useState(false);
    const { confirmAsync } = useConfirmation();
    const { openWindow } = useWindow();

    const handleSubmit = async (values: ILocationItem, actions: formik.FormikHelpers<ILocationItem>) => {
        actions.setStatus();
        try {
            setIsLoading(true);
            await api.administration.updateLocation(values);
            await getLocations(parseInt(organizationInformationId.toString()));
            toast.success('Lokacijos pakeitimai išsaugoti');
            fetchData();
        } catch (ex) {
            actions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            getSelectedLocation(values.id);
            setIsLoading(false);

            actions.setSubmitting(false);
        }
    };

    const fetchData = async () => {
        setLevelOptions(null);
        if (state.selectedLocation && state.selectedLocation.locationType === locationType.Level) {
            var options = await api.administration.getAvailableFloors(state.selectedLocation.partOfLocationId!);
            state.selectedLocation.floor && options.push(state.selectedLocation.floor);
            options = options.sort((a, b) => a - b);
            setLevelOptions(mapFloorsToOptions(options));
        } else {
            setLevelOptions([]);
        }
    };

    useEffect(() => {
        fetchData();
    }, [state.selectedLocation]);

    const mapFloorsToOptions = (floors: number[]): IDropdownOption[] => {
        var floorOptions: IDropdownOption[] = [];

        floors.map((x) => {
            floorOptions.push({
                key: x,
                text: x.toString()
            });
        });
        return floorOptions;
    };

    const handleDelete = async (locationId: number, organizationId: number) => {
        const { success } = await confirmAsync(
            'Lokacijos ištrynimas',
            'Ar tikrai norite ištrinti pasirinktą lokaciją? Ištrynę lokaciją nebegalėsite jos atkurti.'
        );

        if (!success) {
            return;
        }
        await deleteLocation(locationId, organizationId);
        await getLocations(organizationId);
    };

    const openLocationTreeNodeCreateWindow = async (selectedLocation: ILocationItem, initialValues: ILocationItem | null) => {
        try {
            LocationTreeNodeCreateWindow.defaultProps = {
                componentProps: {
                    organizationInformationId: parseInt(organizationInformationId.toString()),
                    refetchLocations: getLocations,
                    selectedLocation: selectedLocation,
                    basedOnInitialValues: initialValues,
                    openLocationTreeNodeCreateWindow: openLocationTreeNodeCreateWindow
                }
            };
            openWindow({
                title: 'Žemesnio lygio lokacijos sukūrimas',
                component: LocationTreeNodeCreateWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            {state.isLocationSelected && !state.selectedLocation && isLoading && <KendoLoading />}
            {state.selectedLocation && levelOptions && (
                <formik.Formik validationSchema={validationSchema} initialValues={{ ...state.selectedLocation }} onSubmit={handleSubmit}>
                    {(formik) => (
                        <Form>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm8 ms-md6 ms-lg6">
                                    <fabric.Label>Lokacijos tipas</fabric.Label>
                                    <fabric.Text style={{ fontWeight: 'lighter' }} variant="xLargePlus">
                                        {locationType.displayTypesArray()[state.selectedLocation!.locationType - 1].text}
                                    </fabric.Text>
                                </div>
                                <div style={{ position: 'relative', float: 'right' }}>
                                    {state.selectedLocation!.locationType !== locationType.Cabinet && (
                                        <>
                                            <ListItemCreateButton
                                                openCreateModal={() => openLocationTreeNodeCreateWindow(state.selectedLocation, null)}
                                                text="Sukurti žemesnio lygio lokaciją"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <hr />
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <Field
                                        name="name"
                                        render={({ field, form }: formik.FieldProps<string>) => (
                                            <fabric.TextField
                                                {...field}
                                                placeholder="Įveskite lokacijos pavadinimą."
                                                label="Lokacijos pavadinimas"
                                                errorMessage={(formik.status && formik.status.name) || (form.errors.name && form.errors.name)}
                                            />
                                        )}
                                    />
                                </div>
                                {state.selectedLocation!.locationType === locationType.Level && levelOptions && (
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <Field
                                            name="floor"
                                            render={({ field, form }: formik.FieldProps<number>) => (
                                                <fabric.Dropdown
                                                    disabled={formik.values.locationType !== locationType.Level}
                                                    label="Aukštas"
                                                    options={levelOptions}
                                                    placeholder="Pasirinkite aukšto skaičių"
                                                    selectedKey={field.value}
                                                    onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                />
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                            {(state.selectedLocation?.locationType === locationType.Cabinet ||
                                state.selectedLocation?.locationType === locationType.Room) && (
                                <>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm5 ms-md5 ms-lg5">
                                            <Field
                                                name="activeTicketPrintingTimeBeforeVisit"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        value={field.value}
                                                        disabled={!form.values.isActiveTicketPrintingTimeBeforeVisitEnabled}
                                                        type="number"
                                                        placeholder="Naudojami bendri nustatymai"
                                                        label="Laikas prieš vizitą minutėmis"
                                                        errorMessage={
                                                            (formik.status && formik.status.activeTicketPrintingTimeBeforeVisit) ||
                                                            (form.errors.activeTicketPrintingTimeBeforeVisit &&
                                                                form.errors.activeTicketPrintingTimeBeforeVisit)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm1 ms-md1 ms-lg1">
                                            <Field
                                                name="isActiveTicketPrintingTimeBeforeVisitEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        {...field}
                                                        styles={toggleStyles}
                                                        defaultChecked={field.value}
                                                        onChange={() => {
                                                            form.setFieldValue(field.name, !field.value);
                                                            if (!field.value === false) {
                                                                form.setFieldValue('activeTicketPrintingTimeBeforeVisit', null);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm5 ms-md5 ms-lg5">
                                            <Field
                                                name="activeTicketPrintingTimeAfterVisit"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        value={field.value}
                                                        disabled={!form.values.isActiveTicketPrintingTimeAfterVisitEnabled}
                                                        type="number"
                                                        placeholder="Naudojami bendri nustatymai"
                                                        label="Laikas po vizito minutėmis"
                                                        errorMessage={
                                                            (formik.status && formik.status.activeTicketPrintingTimeAfterVisit) ||
                                                            (form.errors.activeTicketPrintingTimeAfterVisit &&
                                                                form.errors.activeTicketPrintingTimeAfterVisit)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm1 ms-md1 ms-lg1">
                                            <Field
                                                name="isActiveTicketPrintingTimeAfterVisitEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        {...field}
                                                        defaultChecked={field.value}
                                                        styles={toggleStyles}
                                                        onChange={() => {
                                                            form.setFieldValue(field.name, !field.value);
                                                            if (!field.value === false) {
                                                                form.setFieldValue('activeTicketPrintingTimeAfterVisit', null);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm5 ms-md5 ms-lg5">
                                            <Field
                                                name="key"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        value={field.value}
                                                        placeholder="Lokacijos raktas"
                                                        label="Lokacijos raktas "
                                                        errorMessage={
                                                            (formik.status && formik.status.locationKey) ||
                                                            (form.errors.locationKey && form.errors.locationKey)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm5 ms-md5 ms-lg5">
                                            <Field
                                                name="doorControllerKey"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        {...field}
                                                        value={field.value}
                                                        placeholder="Durų valdiklio raktas"
                                                        label="Durų valdiklio raktas "
                                                        errorMessage={
                                                            (formik.status && formik.status.locationKey) ||
                                                            (form.errors.locationKey && form.errors.locationKey)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="ms-Grid-row" dir="rtl" style={{ paddingTop: '20px' }}>
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <fabric.PrimaryButton
                                        disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                                        type="submit"
                                        text="Išsaugoti"
                                        style={{ marginLeft: '10px' }}
                                    />
                                    <fabric.DefaultButton
                                        disabled={formik.isSubmitting}
                                        onClick={async () =>
                                            await handleDelete(state.selectedLocation!.id, parseInt(organizationInformationId.toString()))
                                        }
                                        styles={{
                                            root: {
                                                backgroundColor: fabric.getTheme().semanticColors.errorBackground,
                                                color: fabric.getTheme().semanticColors.errorText
                                            }
                                        }}
                                        text="Ištrinti"
                                    />
                                </div>
                            </div>
                        </Form>
                    )}
                </formik.Formik>
            )}
        </>
    );
};

export default LocationsPageLocation;
